import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import useSurveyManagementStore from "../stores/surveyManagementStore";

ChartJS.register(ArcElement, Tooltip, Legend);

const SurveyDetails = ({ data }) => {
  const { selectedSurveyDetails, fetchSurveyDetails, isLoading } = useSurveyManagementStore();

  const [chartData, setChartData] = useState(null);
  const [levelsDataState, setLevelsDataState] = useState(null);

  console.log("SurveyDetails data prop:", data);
  console.log("selectedSurveyDetails:", selectedSurveyDetails);

  const severityDistribution = selectedSurveyDetails?.severityDistribution || {};
  const defectAnalysis = selectedSurveyDetails?.defectAnalysis || {};
  const totalDistance = selectedSurveyDetails?.totalDistance || "0 km";

  useEffect(() => {
    if (data?._id) {
      fetchSurveyDetails(data._id);
    }
  }, [data, fetchSurveyDetails]);

  useEffect(() => {
    console.log("severityDistribution:", severityDistribution);

    const newPieChartData = {
      labels: ["Critical", "High", "Medium", "Low", "Normal"],
      datasets: [
        {
          data: [
            severityDistribution.Critical || 0,
            severityDistribution.High || 0,
            severityDistribution.Medium || 0,
            severityDistribution.Low || 0,
            severityDistribution.Normal || 0,
          ],
          backgroundColor: ["#ff6b6b", "#ff9f43", "#feca57", "#1dd1a1", "#10ac84"],
        },
      ],
    };

    const newLevelsData = [
      { Levels: "Critical", "Length (M)": severityDistribution.Critical || 0 },
      { Levels: "High", "Length (M)": severityDistribution.High || 0 },
      { Levels: "Medium", "Length (M)": severityDistribution.Medium || 0 },
      { Levels: "Low", "Length (M)": severityDistribution.Low || 0 },
      { Levels: "Normal", "Length (M)": severityDistribution.Normal || 0 },
    ];

    setChartData(newPieChartData);
    setLevelsDataState(newLevelsData);

    console.log("Updated Pie Chart Data:", newPieChartData);
    console.log("Updated Levels Data:", newLevelsData);
  }, [selectedSurveyDetails]);

  const calculateRmiScore = (severity) => {
    if (!severity) return 0;
    const weights = {
      Critical: 0.4,
      High: 0.3,
      Medium: 0.2,
      Low: 0.1,
      Normal: 0.05,
    };
    const total =
      (severity.Critical || 0) * weights.Critical +
      (severity.High || 0) * weights.High +
      (severity.Medium || 0) * weights.Medium +
      (severity.Low || 0) * weights.Low +
      (severity.Normal || 0) * weights.Normal;
    const maxScore = 100;
    return ((maxScore - total) / 10).toFixed(1);
  };

  const rmiScore = calculateRmiScore(severityDistribution);
  console.log("RMI Score:", rmiScore);

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: {
          boxWidth: 15,
          boxHeight: 15,
          display: "flex",
          flexDirection: "column",
          font: {
            size: 12,
            weight: "bold",
            family: "Arial",
            color: "#000000",
            lineHeight: 1.2,
          },
        },
      },
    },
  };

  const totalLengthKm = parseFloat(totalDistance) || 0;
  console.log("Total Length (KM):", totalLengthKm);

  const defectsData = Object.entries(defectAnalysis || {})
    .filter(([_, count]) => count > 0)
    .map(([defect, count]) => ({
      Defects: defect,
      Count: count,
    }));
  console.log("Defects Data:", defectsData);

  const columnDefs = (levelsDataState && Object.keys(levelsDataState[0]).map((key) => ({
    field: key,
    sortable: true,
    flex: 1,
    width: 200,
  }))) || [];

  const defectColumns = defectsData.length > 0
    ? Object.keys(defectsData[0]).map((key) => ({
        field: key,
        flex: 1,
        sortable: true,
      }))
    : [{ field: "Defects", flex: 1, sortable: true }, { field: "Count", flex: 1, sortable: true }];

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!selectedSurveyDetails) {
    return <div>No survey details available.</div>;
  }

  if (!chartData || !levelsDataState) {
    return <div>Preparing data...</div>;
  }

  return (
    <div className="grid grid-cols-3 gap-6">
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-6">
          <div className="border border-[#1a539c] p-4 text-center">
            <h2 className="text-[#1a539c] text-sm">RMI Score</h2>
            <p className="text-3xl font-bold text-[#1a539c]">{rmiScore}</p>
          </div>
          <div className="border border-[#1a539c] p-4 text-center">
            <h2 className="text-[#1a539c] text-sm">Total Length</h2>
            <p className="text-3xl font-bold text-[#1a539c]">
              {totalLengthKm} KM
            </p>
          </div>
        </div>
        <div className="w-[250px] h-[250px] mx-auto">
          <Pie data={chartData} options={pieChartOptions} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 col-span-2">
        <div>
          <div className="ag-theme-alpine w-full h-[300px]">
            <AgGridReact
              rowHeight={45}
              rowData={levelsDataState}
              columnDefs={columnDefs}
            />
          </div>
        </div>

        <div>
          <div className="ag-theme-alpine w-full h-[300px]">
            <AgGridReact
              rowHeight={45}
              rowData={defectsData}
              columnDefs={defectColumns}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyDetails;